import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { ExportProps } from 'features/report/report-constants';
import TableRowExport from 'component/Pdf/TableRowExport'
import { Card, Col, Row } from 'react-bootstrap'
import { Box, MenuItem, Divider } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { UseSnCode } from 'utils/useSnCode'
/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API */
import ClinicApi, { BranchInterface } from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import FilterSelect from 'component/Select/FilterSelect'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import FucntionPrint from "component/Print/print"

/** REPORT */
import VisitList from './VisitList'
import VisitTotal from './VisitTotal';
import exportVisitListToExcel from './ExportToExcel'

/** CONSTANTS */
import { numberFormat } from 'utils/app.utils'
import { colors } from 'constants/theme'
import { dateToView } from 'utils/date.utils'

/** STYLE */
import * as UseStyled from 'features/report/useStyled'
import { routeName } from 'routes/routes-name'
import ButtonCustom from 'component/Button/ButtonCustom';
import InputTextSearch from 'component/Input/InputTextSearch';
import { notiError } from 'component/notifications/notifications'


export enum APPOINTMENT_STATUS {
    OVERDUE = 'OVERDUE',
    PAID = `PAID`,
    CANCEL_VISIT = `CANCEL_VISIT`,
    CANCEL_BILL = `CANCEL_BILL`
}

type AppliedFilters = {
    branchId: number
    appointmentStatus: string
    dateStart: string | null
    dateEnd: string | null
    search: string
}

/** Shared between Visit (filter chip) and ExportComponant (excel column hide).
 *  Resolves once per component instance — no need to refetch on filter change. */
function useSnType() {
    const [snType, setSnType] = useState(false)
    useEffect(() => {
        let cancelled = false
        UseSnCode().then((sn) => { if (!cancelled) setSnType(sn) })
        return () => { cancelled = true }
    }, [])
    return snType
}

export default function Visit() {
    const { t } = useTranslation()
    const history = useHistory()
    const componentRefPDF = useRef<HTMLDivElement>(null)
    const todayRange = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]

    const [loading, setLoading] = useState<boolean>(true)
    const [branches, setBranches] = useState<BranchInterface[]>([])

    /** "Pending" filter state — drives the UI inputs only; changes do NOT trigger API.
     *  API uses `applied` below, which is snapshotted when Search is clicked. */
    const [branchId, setBranchId] = useState<number>(0)
    const [appointmentStatus, setAppointmentStatus] = useState<string>('ALL')
    const [rangeDate, setRangeDate] = useState<string[]>(todayRange)
    const [searchInput, setSearchInput] = useState<string>('')

    /** "Applied" filter state — the snapshot Search committed to. Changing any of these
     *  re-derives `buildCondition` → fetchVisit/loadVisitExport fire via their useEffect. */
    const [applied, setApplied] = useState<AppliedFilters>({
        branchId: 0,
        appointmentStatus: 'ALL',
        dateStart: todayRange[0],
        dateEnd: todayRange[1],
        search: ''
    })

    const [visitPage, setVisitPage] = useState<number>(1)
    const [visitPageLimit, setVisitPageLimit] = useState<number>(10)
    const [visitRowCount, setVisitRowCount] = useState<number>(0)
    const [visitSortBy, setVisitSortBy] = useState<string>('createAt')
    const [visitSortType, setVisitSortType] = useState<string>('ASC')
    const [visitData, setVisitData] = useState<any>({ rows: [], sum: undefined, total: undefined })
    const [visitExport, setVisitExportData] = useState<any>({ rows: [], sum: undefined, total: undefined })

    const applyFilters = () => {
        setApplied({
            branchId,
            appointmentStatus,
            dateStart: rangeDate[0] ?? null,
            dateEnd: rangeDate[1] ?? null,
            search: searchInput
        })
        setVisitPage(1)
        setLoading(true)
    }

    useEffect(() => {
        ReportApi.reportList({ page: 1, pageLimit: 200 })
            .then(({ data }) => {
                const allowed = _.find(data, (val: any) => String(val.reportKey) === 'VISIT')
                if (!allowed) history.push(routeName.report)
            })

        ClinicApi.findAllBranches()
            .then(({ data }) => setBranches(data))
            .catch(() => { return })
    }, [history])

    /** Build the shared query payload from APPLIED filters (snapshot at Search-click).
     *  fetchVisit and loadVisitExport differ only in pagination and the `export` flag —
     *  centralising avoids the silent drift that caused MJ-2606-0300. */
    const buildCondition = useCallback((extras: Partial<FindAllReportInterface> = {}): FindAllReportInterface => {
        let condition: FindAllReportInterface = {
            page: visitPage,
            pageLimit: visitPageLimit,
            search: applied.search,
            ...extras
        }
        if (applied.branchId) condition = { ...condition, branchId: applied.branchId }
        if (applied.appointmentStatus && applied.appointmentStatus !== 'ALL') {
            condition = { ...condition, status: applied.appointmentStatus }
        }
        if (visitSortType) condition = { ...condition, sortType: visitSortType }
        if (visitSortBy) condition = { ...condition, sortBy: visitSortBy }
        if (applied.dateStart !== null && applied.dateEnd !== null) {
            condition = { ...condition, dateStart: applied.dateStart, dateEnd: applied.dateEnd }
        }
        return condition
    }, [visitPage, visitPageLimit, applied, visitSortType, visitSortBy])

    const fetchVisit = useCallback(() => {
        ReportApi.reportVisit(buildCondition())
            .then(({ headers, data }) => {
                setVisitData(data)
                setVisitRowCount(headers['x-total'])
            })
            .catch(() => {
                setVisitData({ rows: [], sum: undefined, total: undefined })
                setVisitRowCount(0)
                notiError(t('REPORT.MESSAGE.ERROR'))
            })
            .finally(() => setLoading(false))
    }, [buildCondition, t])

    const loadVisitExport = useCallback(() => {
        ReportApi.reportVisit(buildCondition({ page: 1, pageLimit: 10000, export: 1 }))
            .then(({ data }) => setVisitExportData(data))
            .catch(() => setVisitExportData({ rows: [], sum: undefined, total: undefined }))
    }, [buildCondition])

    useEffect(() => { fetchVisit() }, [fetchVisit])
    useEffect(() => { loadVisitExport() }, [loadVisitExport])

    const onChangeVisitSort = (sortByVal: string, sortTypeVal: string) => {
        setVisitSortType(sortTypeVal)
        setVisitSortBy(sortByVal)
    }
    const onChangeVisitRowPerPage = (limit: number) => setVisitPageLimit(limit)
    const onChangeVisitPage = (val: number) => setVisitPage(val)

    const handleExcelClick = async () => {
        try {
            setLoading(true)
            const { data } = await ReportApi.reportVisit(buildCondition({ page: 1, pageLimit: 10000, export: 1 }))
            await exportVisitListToExcel({ data, applied, branches, t })
        } catch {
            notiError(t('REPORT.MESSAGE.ERROR'))
        } finally {
            setLoading(false)
        }
    }

    /** Print/Export headers reflect APPLIED filters — what the table actually shows. */
    const appliedBranchLabel = applied.branchId === 0
        ? ` ${t('REPORT.REPORT_GROUP.ALL')}`
        : `${_.get(_.find(branches, { branchId: applied.branchId }), 'branchName')}`

    return (
        <Card className={'border-0 h-100'}>
            <HeaderCard text={t(`REPORT.REPORT_NAME.VISIT`)} />
            <Card.Body>
                <Card.Title className={'mb-0'}>
                    <Row className='mx-0'>
                        <Col sm={12} md={6} lg={5} xl={3} xxl={2} className={'pt-2'}>
                            <InputTextSearch
                                keyInput={'search'}
                                label={t('REPORT.REPORT_NAME.SEARCH_VISIT')}
                                value={searchInput}
                                onchange={(event) => setSearchInput(event.target.value)}
                                onClear={() => setSearchInput('')}
                            />
                        </Col>
                        <Col sm={6} md={4} xl={3} className={'pt-2 px-1'}>
                            <FilterSelect
                                onchange={(e) => setBranchId(e.target.value)}
                                renderValue={() => branchId === 0
                                    ? `${t('REPORT.FILTER.BRANCH')}: ${t('REPORT.REPORT_GROUP.ALL')}`
                                    : `${t('REPORT.FILTER.BRANCH')}: ${_.get(_.find(branches, { branchId }), 'branchName')} `}
                                label={''}
                                selectId="select-branch"
                                labelId="label-branch"
                                value={branchId}
                                options={[
                                    <MenuItem key={0} value={0}>{t('REPORT.REPORT_GROUP.ALL')}</MenuItem>,
                                    ..._.map(branches, (b: BranchInterface) => (
                                        <MenuItem key={b.branchId} value={b.branchId}>
                                            {b.branchName || b.branchNameEn}
                                        </MenuItem>
                                    ))
                                ]}
                                formControlStyle={{ maxWidth: 'unset !important' }}
                                classesOption="style-select-doctors"
                            />
                        </Col>
                        <Col sm={6} md={4} xl={3} className={'pt-2 px-1'}>
                            <FilterSelect
                                onchange={(e) => setAppointmentStatus(e.target.value)}
                                renderValue={() => appointmentStatus === 'ALL'
                                    ? `${t('REPORT.FILTER.APPOINTMENT_STATUS')}: ${t('REPORT.REPORT_GROUP.ALL')}`
                                    : `${t('REPORT.FILTER.APPOINTMENT_STATUS')}: ${t(`REPORT.VISIT_STATUS.${appointmentStatus}`)}`
                                }
                                label={''}
                                selectId="select-appointment-status"
                                labelId="label-appointment-status"
                                value={appointmentStatus}
                                options={[
                                    <MenuItem key="ALL" value="ALL">{t('REPORT.FILTER.ALL')}</MenuItem>,
                                    ...Object.values(APPOINTMENT_STATUS).map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {t(`REPORT.VISIT_STATUS.${status}`)}
                                        </MenuItem>
                                    ))
                                ]}
                                formControlStyle={{ maxWidth: 'unset !important' }}
                                classesOption="style-select-doctors"
                            />
                        </Col>
                        <Col sm={6} md={4} xl={3} className={'pt-2 px-1'}>
                            <InputSecondNewRangePicker
                                inputHeight={32}
                                value={rangeDate || emptyRangePicker}
                                onchange={(val: any) => {
                                    if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                                    else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                                }}
                                label={''}
                            />
                        </Col>
                        <Col md={1} xl={1} className={'pt-0'}>
                            <ButtonCustom
                                onClick={applyFilters}
                                textButton={t('INPUT.SEARCH')}
                                style={{ height: '30px', marginTop: '5px' }}
                            />
                        </Col>
                        <Col md={4} xl={3} className={'d-xl-flex justify-content-xl-end ml-xl-auto pt-2 px-1'}>
                            <UseStyled.DropdownDocument title={t('PRINT_DOCUMENT')} id="dropdown-basic" className='ml-xl-auto'>
                                <UseStyled.DropdownItem onClick={handleExcelClick}>{t('Excel')}</UseStyled.DropdownItem>
                                <FucntionPrint
                                    content={() => componentRefPDF.current}
                                    docTitle={t(`REPORT.REPORT_NAME.VISIT`)}
                                    removeIfram
                                    trigger={() => <UseStyled.DropdownItem>{t('PDF')}</UseStyled.DropdownItem>}
                                    style={`@page { size: 1122.519685px 793.7007874px ;  margin: 0.5cm; } @media print { body { -webkit-print-color-adjust: exact; }`}
                                />
                            </UseStyled.DropdownDocument>
                        </Col>
                    </Row>
                </Card.Title>

                <Box className={'mt-3 pl-xl-3 pr-xl-2 pb-4'}>
                    <VisitList
                        page={visitPage}
                        pageLimit={visitPageLimit}
                        rowCount={visitRowCount}
                        sortBy={visitSortBy}
                        sortType={visitSortType}
                        data={visitData}
                        onRequestSort={onChangeVisitSort}
                        handleChangeRowsPerPage={onChangeVisitRowPerPage}
                        handleChangePage={onChangeVisitPage}
                    />
                </Box>
                <Divider className="mx-0 mt-3 mb-4" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />
                <Box className={'mt-3 pl-xl-3 pr-xl-2 pb-4 d-flex'}>
                    <Box className={'mt-3 pl-xl-3 pr-xl-2 pb-4'}>
                        <VisitTotal
                            page={1}
                            pageLimit={10}
                            rowCount={0}
                            sortBy={''}
                            sortType={''}
                            data={visitData}
                            onRequestSort={() => { return }}
                            handleChangeRowsPerPage={() => { return }}
                            handleChangePage={() => { return }}
                        />
                    </Box>
                </Box>
            </Card.Body>
            <Loading show={loading} type='fullLoading' />

            <div className="print-show" ref={componentRefPDF}>
                <ExportData
                    headCells={t(`REPORT.REPORT_NAME.VISIT`)}
                    componant={
                        <>
                            <UseStyled.Styles className="pt-3">
                                <div style={{ fontSize: "14px" }}>
                                    <span>{t('REPORT.FILTER.BRANCH')}: </span>
                                    <span style={{ marginRight: "13px" }}>{appliedBranchLabel}</span>
                                    <span>{t('REPORT.FILTER.APPOINTMENT_STATUS')} : </span>
                                    <span style={{ marginRight: "13px" }}>
                                        {applied.appointmentStatus === 'ALL' ? t(`REPORT.FILTER.ALL`) : t(`REPORT.VISIT_STATUS.${applied.appointmentStatus}`)}
                                    </span>
                                    <span>{t('REPORT.FILTER.DAY')} </span>
                                    <span style={{ marginRight: "5px" }}>{applied.dateStart && applied.dateStart !== "Invalid date" ? dateToView(applied.dateStart) : ""}</span>
                                    <span>{t('REPORT.FILTER.TO')} </span>
                                    <span>{applied.dateEnd && applied.dateEnd !== "Invalid date" ? dateToView(applied.dateEnd) : ""}</span>
                                </div>
                            </UseStyled.Styles>
                            <ExportComponant visitExport={visitExport} />
                        </>
                    }
                />
            </div>
        </Card>
    )
}

function ExportData(props: ExportProps) {
    return (
        <>
            <div className='large'>{props.headCells}</div>
            {props.componant}
        </>
    )
}

function ExportComponant({ visitExport }: { visitExport: any }) {
    const { t } = useTranslation()
    const snType = useSnType()

    const headCellVisit = [
        { labelKey: 'REPORT.TABLE.VISIT.DATE', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.VN', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.CN_NUMBER', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.BRANCH_CN_NUMBER', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.SN_CODE', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.PATIENT_NAME', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.PHONE', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.PAYMENT_TOTAL', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.PAYMENT_PAY', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.PAYMENT_OVERDUE', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.APPOINTMENT_STATUS', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.SEND_BY', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.CANCEL_BY', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.REMARK_CANCEL', align: 'center' },
        { labelKey: 'REPORT.TABLE.VISIT.TRANSACTION_BY', align: 'center' }
    ]
    if (!snType) headCellVisit.splice(4, 1)

    const headCellVisitTotal = [
        { labelKey: 'REPORT.TABLE.VISIT.APPOINTMENT_STATUS', align: 'center' },
        { labelKey: t('REPORT.VISIT_STATUS.PAID'), align: 'center' },
        { labelKey: t('REPORT.VISIT_STATUS.CANCEL_BILL'), align: 'center' },
        { labelKey: t('REPORT.VISIT_STATUS.OVERDUE'), align: 'center' },
        { labelKey: t('REPORT.VISIT_STATUS.CANCEL_VISIT'), align: 'center' }
    ]

    const renderVisitTotalExport = (data: any, dataType: "sum" | "total") => {
        if (!data) return null
        const label = dataType === "sum" ? t('REPORT.UNIT.QTY') : t('REPORT.UNIT.QTY_PAYMENT')
        const cell = (v: any) => dataType === 'total' ? (numberFormat(v) || '-') : (v || '-')
        return (
            <tr>
                <td style={{ textAlign: 'center', fontSize: '9px' }}>{label}</td>
                <td style={{ textAlign: 'center', fontSize: '9px' }}>{cell(data.paid)}</td>
                <td style={{ textAlign: 'center', fontSize: '9px' }}>{cell(data.cancelBill)}</td>
                <td style={{ textAlign: 'center', fontSize: '9px' }}>{cell(data.overdue)}</td>
                <td style={{ textAlign: 'center', fontSize: '9px' }}>{dataType === 'total' ? '-' : (data.cancelVisit || '-')}</td>
            </tr>
        )
    }

    const renderVisitExport = (data: any, no: number) => {
        const cancelled = data.visitStatus === "CANCEL_VISIT"
        const columns = [
            { option: 'TEXT', align: 'left', label: dateToView(data.updatedAt), class: 'text-nowrap' },
            { option: 'TEXT', align: 'left', label: data.vnNumber, class: 'text-nowrap' },
            { option: 'TEXT', align: 'left', label: data.cnNumber || '-', class: 'text-nowrap' },
            { option: 'TEXT', align: 'left', label: data.branchCnNumber || '-', class: 'text-nowrap' },
            { option: 'TEXT', align: 'left', label: data.snCode || '-', class: 'text-nowrap' },
            { option: 'TEXT', align: 'center', label: data.fullName },
            { option: 'TEXT', align: 'left', label: data.phone },
            { option: 'TEXT', align: 'right', label: cancelled ? '-' : numberFormat(data.totalPrice), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: cancelled ? '-' : numberFormat(data.amountPaid), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', label: cancelled ? '-' : numberFormat(data.overdue), class: 'text-nowrap' },
            { option: 'TEXT', align: 'center', label: t(`REPORT.VISIT_STATUS.${data.visitStatus}`), class: 'text-nowrap' },
            { option: 'TEXT', align: 'center', label: data.updatedBy || '-' },
            { option: 'TEXT', align: 'center', label: data.cancelBy || '-' },
            { option: 'TEXT', align: 'center', label: data.billRemark || '-' },
            { option: 'TEXT', align: 'center', label: cancelled ? '-' : data.updatedBillBy }
        ]
        if (!snType) columns.splice(4, 1)
        return (
            <TableRowExport
                key={no.toString()}
                id={no.toString()}
                obj={data}
                columns={columns}
            />
        )
    }

    return (
        <>
            <UseStyled.Styles className="pt-3">
                <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: '100%', maxWidth: '100%' }}>
                    <thead style={{ fontSize: '8px' }}>
                        <tr>
                            {_.map(headCellVisit, (val: any, idx: number) => (
                                <td key={idx} style={{ textAlign: val.align, fontSize: 7, fontWeight: 500 }}> {t(val.labelKey)}</td>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {_.map(visitExport?.rows, (d: any, index: number) => renderVisitExport(d, index))}
                    </tbody>
                </table>
            </UseStyled.Styles>
            <br />
            <UseStyled.Styles className="pt-3">
                <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: '50%', maxWidth: '50%', border: 'none !important' }}>
                    <thead>
                        <tr>
                            {_.map(headCellVisitTotal, (val: any, idx: number) => (
                                <td key={idx} style={{ textAlign: val.align, fontSize: 8, fontWeight: 300 }}> {t(val.labelKey)}</td>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visitExport?.sum && renderVisitTotalExport(visitExport.sum, 'sum')}
                        {visitExport?.total && renderVisitTotalExport(visitExport.total, 'total')}
                    </tbody>
                </table>
            </UseStyled.Styles>
        </>
    )
}
